import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectIsVerifyingUserFromState,
  selectResendVerificationTopLevelErrorFromState,
  selectVerificationEmailResentFromState,
  selectVerificationPhoneLoadingFromState,
  selectVerificationPhoneResentFromState,
  selectVerifyEmailTopLevelErrorFromState,
  selectVerifyPhoneTopLevelErrorFromState,
} from 'src/app/Signup/redux/signup.selectors'
import Budgets from 'src/assets/images/Budgets.png'
import Cards from 'src/assets/images/Cards.png'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Popup from 'src/components/Popup/Popup'
import VerifyForm from 'src/components/VerifyForm/VerifyForm'
import { SupportedBrands, UserStatus } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { useUserSkipPhoneVerification } from 'src/queries/users.queries'
import { createSetIsExtraVerificationPopupOpen } from 'src/redux/actions/app.actions'
import {
  createSetUserStatus,
  POST_USER_VERIFICATION_SUCCESS,
  postAuthType,
  postPhoneVerificationAndSignIn,
  postSendPhoneVerificationCode,
  postUserResendVerification,
  postUserVerification,
  postUserWithoutPhoneVerificationAndSignIn,
} from 'src/redux/actions/auth.actions'
import {
  selectAuthTypeFromState,
  selectEmailFromState,
  selectPhoneNumberResponseValueFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectBrandName } from 'src/redux/selectors/session.selectors'
import {
  formatMaskedPhoneNumber,
  formatPhoneNumber,
} from 'src/utils/string.utils'
import { css } from 'styled-components'

interface ExtraVerificationPopupProps {
  email?: string
  password: string
  phoneIsoCountry?: string
  phone?: string
  step?: number
  isPhoneNumberVerificationOptional?: boolean
  defaultWrongEmailText?: (val?: UserStatus) => void
  isVerifyingUser?: boolean
  verifyTopLevelError?: string
  verificationResent?: boolean
  resendVerificationTopLevelError?: string
  'data-testid'?: string
}

const ExtraVerificationPopup: React.FC<ExtraVerificationPopupProps> = (
  props,
) => {
  const queryParams = useSelector(selectQueryParams)
  const { orgName, inviteeEmail, inviterName, inviterEmail } = queryParams
  const signupAndJoin = !!(
    orgName &&
    inviteeEmail &&
    inviterName &&
    inviterEmail
  )

  const email = useSelector(selectEmailFromState)
  const verificationEmailResent = useSelector(
    selectVerificationEmailResentFromState,
  )
  const verificationPhoneResent = useSelector(
    selectVerificationPhoneResentFromState,
  )
  const resendVerificationTopLevelError = useSelector(
    selectResendVerificationTopLevelErrorFromState,
  )
  const verifyEmailTopLevelError = useSelector(
    selectVerifyEmailTopLevelErrorFromState,
  )
  const verifyPhoneTopLevelError = useSelector(
    selectVerifyPhoneTopLevelErrorFromState,
  )
  const verifyPhoneLoading = useSelector(
    selectVerificationPhoneLoadingFromState,
  )
  const verifyEmailLoading = useSelector(selectIsVerifyingUserFromState)
  const getPhoneNumberResponseValue = useSelector(
    selectPhoneNumberResponseValueFromState,
  )
  const authType = useSelector(selectAuthTypeFromState)
  const hideImages = useSelector(selectBrandName) === SupportedBrands.Jpm
  const { analytics } = useAnalyticsContext()

  const phoneNumber =
    props.phoneIsoCountry && props.phone
      ? `+${props.phoneIsoCountry}${props.phone}`
      : undefined

  const [pressedSkipForNow, setPressedSkipForNow] = useState<boolean>(false)
  const [step, setStep] = useState(props.step ?? 1)

  const dispatch = useBackgroundDispatch()

  const dispatchSetUserStatus = (val?: UserStatus): void => {
    dispatch(createSetUserStatus(val))
  }

  const dispatchPostUserResendVerification = (val: string): void => {
    dispatch(postUserResendVerification(val))
  }

  const dispatchPostResendPhoneVerificationCode = (val: string): void => {
    dispatch(postSendPhoneVerificationCode(val, true))
  }

  const dispatchPostEmailVerification = (code: string): void => {
    if (!authType) {
      dispatch(
        postAuthType({
          data: email,
        }),
      )
    }
    dispatch(postUserVerification(code, email)).then((res): void => {
      if (
        res.type === POST_USER_VERIFICATION_SUCCESS &&
        res.payload.msg === UserStatus.EmailVerified
      ) {
        dispatch(postSendPhoneVerificationCode(email))
        dispatch(createSetIsExtraVerificationPopupOpen(true))
        setStep(2)
      } else if (
        res.type === POST_USER_VERIFICATION_SUCCESS &&
        res.payload.msg === UserStatus.Verified
      ) {
        dispatch(
          postUserWithoutPhoneVerificationAndSignIn(
            email,
            props.password,
            signupAndJoin,
          ),
        )
      }
    })
  }

  const dispatchPostPhoneVerificationThenSignin = (code: string): void => {
    if (!authType) {
      dispatch(
        postAuthType({
          data: email,
        }),
      )
    }
    dispatch(
      postPhoneVerificationAndSignIn(
        email,
        code,
        props.password,
        signupAndJoin,
        false,
      ),
    )
  }

  const { mutate: skipPhoneVerification } = useUserSkipPhoneVerification(
    email,
    props.password,
    signupAndJoin,
  )

  return (
    <Popup
      onClose={(): void =>
        dispatch(createSetIsExtraVerificationPopupOpen(false))
      }
      data-testid={props['data-testid']}
      noPadding
      css={css`
        position: relative;
        overflow: hidden;
      `}
      hideExit
    >
      {step === 1 ? (
        <VerifyForm
          css={css`
            padding: 60px;
          `}
          id={'verifyEmail'}
          data-testid="verifyEmailPopup"
          email={email}
          password={props.password}
          title={<Trns source={'Verify Email'} />}
          subtitle={
            <p>
              <Trns
                source={'Enter the verification code sent to ${destination}'}
                expressions={{
                  destination: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {email}
                    </span>
                  ),
                }}
              />
            </p>
          }
          hideWrongEmailText={signupAndJoin}
          wrongEmailText={
            <Trns
              source={'Wrong email? ${goBack}'}
              expressions={{
                goBack: (
                  <HoverableLink
                    underline
                    size={HoverableLinkSizes.Medium}
                    onClick={(): void => {
                      props.defaultWrongEmailText
                        ? props.defaultWrongEmailText()
                        : dispatch(createSetIsExtraVerificationPopupOpen(false))
                    }}
                    ariaLabelSource={'Go back'}
                  >
                    <Trns source={'Go back'} />
                  </HoverableLink>
                ),
              }}
            />
          }
          buttonText={<Trns source={'Verify Email'} />}
          isVerifyingUser={props.isVerifyingUser ?? verifyEmailLoading}
          verifyTopLevelError={
            props.verifyTopLevelError ?? verifyEmailTopLevelError
          }
          resendVerificationTopLevelError={
            props.resendVerificationTopLevelError ??
            resendVerificationTopLevelError
          }
          verificationResent={
            props.verificationResent ?? verificationEmailResent
          }
          setStatus={dispatchSetUserStatus}
          postVerification={dispatchPostEmailVerification}
          postResendVerification={dispatchPostUserResendVerification}
        />
      ) : (
        (props.phone || (props.step && props.step === 2)) && (
          <VerifyForm
            css={css`
              padding: 60px;
            `}
            id={'verifyPhoneNumber'}
            data-testid="verifyPhonePopup"
            email={email}
            title={<Trns source={'Verify Phone Number'} />}
            subtitle={
              <p>
                <Trns
                  source={'Enter the verification code sent to ${destination}'}
                  expressions={{
                    destination: (
                      <span
                        css={css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )}
                        `}
                      >
                        {phoneNumber ? (
                          formatPhoneNumber(phoneNumber)
                        ) : getPhoneNumberResponseValue ? (
                          formatMaskedPhoneNumber(getPhoneNumberResponseValue)
                        ) : (
                          <Trns source={'Phone Number'} />
                        )}
                      </span>
                    ),
                  }}
                />
              </p>
            }
            wrongEmailText={
              <>
                <Trns
                  source={'Wrong phone number? ${skipForNow}'}
                  expressions={{
                    skipForNow: (
                      <HoverableLink
                        underline
                        size={HoverableLinkSizes.Medium}
                        onClick={(): void => {
                          setPressedSkipForNow(true)
                          skipPhoneVerification(
                            { body: { email: email } },
                            {
                              onSuccess: () => setPressedSkipForNow(false),
                            },
                          )
                          analytics?.track('phone number - skip verification')
                        }}
                        ariaLabelSource={'Skip for now'}
                        testId={'skipPhoneVerification'}
                      >
                        <Trns source={'Skip for now'} />
                      </HoverableLink>
                    ),
                  }}
                />
              </>
            }
            buttonText={<Trns source={'Verify Phone Number'} />}
            isVerifyingUser={
              props.isVerifyingUser || verifyPhoneLoading || pressedSkipForNow
            }
            verifyTopLevelError={
              props.verifyTopLevelError ?? verifyPhoneTopLevelError
            }
            verificationResent={
              props.verificationResent ?? verificationPhoneResent
            }
            resendVerificationTopLevelError={
              props.resendVerificationTopLevelError ??
              resendVerificationTopLevelError
            }
            setStatus={dispatchSetUserStatus}
            postVerification={dispatchPostPhoneVerificationThenSignin}
            postResendVerification={dispatchPostResendPhoneVerificationCode}
            verifyPhoneNumberPopup
          />
        )
      )}
      {!hideImages && (
        <>
          <div
            css={css`
              position: absolute;
              top: -80px;
              overflow: hidden;
              transform: scale(calc(0.325));
              transform-origin: top right;
            `}
          >
            <img src={Cards} alt={'Cards'} />
          </div>
          <div
            css={css`
              position: absolute;
              bottom: -120px;
              left: -100px;
              overflow: hidden;
              transform: scale(calc(0.325));
              transform-origin: bottom left;
            `}
          >
            <img src={Budgets} alt={'Budgets'} />
          </div>
        </>
      )}
    </Popup>
  )
}

export default ExtraVerificationPopup
